import { useMemo } from 'react';
import { SHIPPING_EXCLUDED_STATES } from '~/constants';
import { useUserDeliveryInfo } from '~/contexts/InventoryContext';
import { useUserLocation } from '~/contexts/UserContext';
import { useVehicleContext } from '~/contexts/VehicleContext';
import type { Vehicle } from '~/contracts';
import {
  formatDateAndMonth,
  getDeliveryOrPickupInfo,
  getPurchaseStatus,
  getVehicleAvailability,
  maybeHasGreatDealTag
} from './ResultTile.helpers';

export const useFreeShippingEligible = () => {
  const { isOutOfMarket = false, isDeliveryAllowed = false } =
    useUserDeliveryInfo() ?? {};

  return !isOutOfMarket && isDeliveryAllowed;
};

export const useShippingCost = () => {
  const { vehicle: { transportCost = 0 } = {} } = useVehicleContext();

  return transportCost;
};

export const useHasNoShipping = () => {
  const { state = 'AZ' } = useUserLocation();

  return SHIPPING_EXCLUDED_STATES.includes(state.toUpperCase());
};

export const useHideShipping = () => {
  const { vehicle } = useVehicleContext();
  const hasShippingRestriction = useHasNoShipping();
  const isLocalDeliveryOnly =
    vehicle.deliveryInfo == null ||
    vehicle.deliveryInfo.activityType === 'LocalDelivery';

  return hasShippingRestriction || !isLocalDeliveryOnly;
};

const TWO_HOURS = 7200;

export const usePurchaseStatus = () => {
  const { vehicle } = useVehicleContext();

  return useMemo(() => {
    if (!vehicle?.vehicleUnlockDateTime) {
      return { isLocked: false, isOnHold: false };
    }

    const timeLeft = Date.parse(vehicle?.vehicleUnlockDateTime) - +new Date();

    return {
      isLocked: Math.floor(timeLeft / 1000) >= TWO_HOURS,
      isOnHold: Math.floor(timeLeft / 1000) < TWO_HOURS
    };
  }, [vehicle.vehicleUnlockDateTime]);
};

export const useHideGetItBy = (getItBy: string) => {
  const { vehicle } = useVehicleContext();
  const { isLocked, isOnHold } = usePurchaseStatus();

  return Boolean(isLocked || isOnHold || vehicle?.isOnDemand || !getItBy);
};

enum DaysRange {
  SameDayDelivery,
  UnderFourDays,
  Tomorrow,
  OverFourDays,
  Unknown
}

function getDaysRange(daysUntilDeliveryOrPickup: number) {
  if (daysUntilDeliveryOrPickup < 1) return DaysRange.SameDayDelivery;
  if (daysUntilDeliveryOrPickup === 1) return DaysRange.Tomorrow;
  if (daysUntilDeliveryOrPickup > 1 && daysUntilDeliveryOrPickup <= 4)
    return DaysRange.UnderFourDays;
  if (daysUntilDeliveryOrPickup > 4) return DaysRange.OverFourDays;

  return DaysRange.Unknown;
}

export const useDeliveryOrPickupTagInfo = () => {
  const { vehicle } = useVehicleContext();

  const { daysTillDeliveryOrPickup, dayAvailable } =
    getDeliveryOrPickupInfo(vehicle);
  const { isLocked, isOnHold } = getPurchaseStatus(vehicle);
  const isAvailable = getVehicleAvailability(vehicle);

  const tagTextMap = {
    [DaysRange.SameDayDelivery]: 'Get it Today',
    [DaysRange.UnderFourDays]: `Get it ${dayAvailable}`,
    [DaysRange.Tomorrow]: 'Get it Tomorrow',
    [DaysRange.OverFourDays]: '',
    [DaysRange.Unknown]: ''
  };
  const textToDisplay =
    daysTillDeliveryOrPickup !== undefined
      ? tagTextMap[getDaysRange(daysTillDeliveryOrPickup)]
      : '';

  const showTag = Boolean(
    !isLocked &&
      !isOnHold &&
      isAvailable &&
      daysTillDeliveryOrPickup !== undefined &&
      textToDisplay.length > 0
  );
  const hasGreatDealTag = maybeHasGreatDealTag(vehicle);

  return { hasGreatDealTag, showTag, textToDisplay };
};

export function getDeliveryOrPickupByVehicle(vehicle: Partial<Vehicle>): {
  activityType: string;
  dateAvailable: string;
  getItBy: string;
  routeId: string;
} {
  const {
    activityType = '',
    dateAvailable,
    dayAvailable,
    daysTillDeliveryOrPickup,
    routeId = ''
  } = getDeliveryOrPickupInfo(vehicle);

  if (!dateAvailable || daysTillDeliveryOrPickup === undefined)
    return { activityType: '', dateAvailable: '', getItBy: '', routeId: '' };

  const chipTextMap = {
    [DaysRange.SameDayDelivery]: 'Today',
    [DaysRange.UnderFourDays]: dayAvailable,
    [DaysRange.Tomorrow]: 'Tomorrow',
    [DaysRange.OverFourDays]: formatDateAndMonth(dateAvailable),
    [DaysRange.Unknown]: ''
  };

  return {
    activityType,
    dateAvailable,
    getItBy: chipTextMap[getDaysRange(daysTillDeliveryOrPickup)] ?? '',
    routeId
  };
}

export function useDeliveryOrPickupChipGetItBy(): ReturnType<
  typeof getDeliveryOrPickupByVehicle
> {
  const { vehicle } = useVehicleContext();

  return getDeliveryOrPickupByVehicle(vehicle);
}

export function useShowSkeleton() {
  const { vehicle } = useVehicleContext();

  return !vehicle?.vehicleId;
}

type VehicleDiscount = {
  amount: number;
  type: string;
};

export const useDiscount = () => {
  const { vehicle } = useVehicleContext();

  const discountedPrice = vehicle?.price?.total ?? 99999;
  const totalDiscount = vehicle?.price?.totalDiscountAmount ?? 0;

  return useMemo(() => {
    const discounts = vehicle?.price?.discounts ?? [];
    const tradeIncentiveDiscount = discounts.find(
      (discount: VehicleDiscount) => discount?.type === 'TradeIncentive'
    );
    const hasOtherDiscount = discounts.find(
      (discount: VehicleDiscount) => discount?.type !== 'TradeIncentive'
    );
    const tradeIncentive = tradeIncentiveDiscount?.amount ?? 0;

    return {
      discounts,
      discountedPrice,
      hasDiscount: totalDiscount > 0,
      hasOtherDiscount,
      totalDiscount,
      tradeIncentive
    };
  }, [discountedPrice, totalDiscount, vehicle?.price?.discounts]);
};
